/* eslint-disable react-hooks/exhaustive-deps */
import { styled } from "@mui/material";
import { Box } from "@mui/system";
import { createContext, useEffect } from "react";
import { ROUTES, APP_VERSION } from "consts";
import { Navigate, Outlet, Route, Routes, useLocation } from "react-router-dom";
import { CreatePage, ExplorerPage, DesignSystemPage } from "pages";
import { useJettonLogo } from "hooks/useJettonLogo";
import { CallPage } from "pages/call";
import { CabalIdPage } from "pages/cabal-id";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { Container } from "components/container";
import { OnboardingViewV2, OnboardingViewV3 } from "components/OnBoarding";
import { TradePage } from "pages/trade";
import { useEmailAuth } from "hooks/useEmailAuth";
import { useTurnkey } from "@turnkey/sdk-react";
import useUserStoreV2 from "store/user-store-v2/useUserStoreV2";
import useNotification from "hooks/useNotification";
import analytics from "services/analytics";
import { ReferralPage } from "pages/referral";
import { Responsive } from "components/Base/ResponsiveLayout";
import { DesktopLayout } from "layout/DesktopLayout";
import { HeaderToast } from "components/header/headerToast";
import useToastStore from "store/toast-store/useToastStore";
import { LayoutPage } from "pages/layout";
import RootLayout from "v2/layouts/RootLayout";
import { AppSideBar } from "v2/components/AppSideBar/AppSideBar";
import { GroupPage } from "v2/pages/group";
import { TradePageV2 } from "v2/pages/trade";
import GroupIdPage from "v2/pages/group-id";
import { TokenIdPage } from "v2/pages/token-id";
import Footer from "v2/components/Footer/ui";
import { Header } from "v2/components/Header/ui";
import PortfolioPage from "v2/pages/portfolio";
import { ReferralPage as ReferralPageV2 } from "v2/pages/referral";
import { Airdrop } from "v2/pages/airdrop";
import { AirdropGroup } from "v2/pages/airdropGroup";
import ShillDashboard from "v2/pages/shill";
import { RedirectPage } from "v2/pages/redirect";
// import eruda from "eruda";

// eruda.init();

analytics.init();
const ExclueFooterRoutes = [ROUTES.onboarding];

const AppWrapper = styled(Box)(() => ({
  display: "flex",
  flexDirection: "column",
  height: "100dvh",
  backgroundColor: "#000000",
}));

const FooterBox = styled(Box)(() => ({
  display: "flex",
  alignItems: "flex-end",
  justifyContent: "center",
  flex: "0 0 auto",
}));

const ScreensWrapper = styled(Box)({
  flex: "1 1 auto",
  overflowY: "hidden",
  overflowX: "hidden",
  width: "100%",
  "-ms-overflow-style": "none" /* Internet Explorer 10+ */,
  "scrollbar-width": "none" /* Firefox */,

  "*::-webkit-scrollbar": {
    display: "none",
  },
  "*::-webkit-scrollbar-track": {
    display: "none",
  },
  "*::-webkit-scrollbar-thumb": {
    display: "none",
  },

  display: "flex",
  flexDirection: "column",
});

export const EnvContext = createContext({
  isSandbox: false,
  isTestnet: false,
});

const PageNotFound = () => {
  const { showNotification } = useNotification();

  useEffect(() => {
    showNotification("Page not found", "error");
  }, [showNotification]);

  return <Box />;
};

interface ContentWrapperProps {
  children?: any;
}

const ContentWrapper = ({ children }: ContentWrapperProps) => {
  return (
    <RootLayout SideBar={<AppSideBar />}>
      {children}
      <Outlet />
    </RootLayout>
  );
};

declare global {
  interface Window {
    Telegram: any;
  }
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false, // default: true
    },
  },
});

const App = () => {
  const { resetJetton } = useJettonLogo();
  const { getTgUserIdV2, logoutTgUserV2 } = useUserStoreV2();
  const { getAuthClient } = useEmailAuth();
  const { turnkey } = useTurnkey();

  const location = useLocation();

  // useRedeemCode({ isAutoCheck: true });
  // useConsumeStartParams();

  useEffect(() => {
    const init = async () => {
      try {
        resetJetton();
        await getTgUserIdV2(false);
        await getAuthClient();
      } catch (error) {
        console.error(error);
      }
    };

    if (turnkey) {
      init();
    }
  }, [getTgUserIdV2, resetJetton, turnkey]);

  useEffect(() => {
    (async () => {
      const appVersion = localStorage.getItem("appVersion");

      if (!appVersion) {
        logoutTgUserV2();
        localStorage.setItem("appVersion", APP_VERSION);
      }
    })();
  }, []);

  const { toastState, resetToast } = useToastStore();

  return (
    <QueryClientProvider client={queryClient}>
      <EnvContext.Provider
        value={{
          isSandbox: window.location.search.includes("sandbox"),
          isTestnet: window.location.search.includes("testnet"),
        }}>
        <AppWrapper>
          <ScreensWrapper>
            <Routes>
              <Route
                path="*"
                element={
                  <Responsive.Layout>
                    <Responsive.View to="md">
                      <Header />
                    </Responsive.View>
                    <Navigate to="/" />
                    <PageNotFound />
                  </Responsive.Layout>
                }
              />
              <Route
                path="/"
                element={
                  <Responsive.Layout>
                    <Responsive.View to="md">
                      <Header />
                    </Responsive.View>
                    <Responsive.View from="md">
                      <Outlet />
                    </Responsive.View>
                    <HeaderToast
                      isOpen={!!toastState.message}
                      variant={toastState.variant}
                      message={toastState.message}
                      onClose={resetToast}
                    />
                  </Responsive.Layout>
                }>
                {/* <Route path={ROUTES.gated} element={<Navigate to={ROUTES.trade} />} /> */}
                <Route path={ROUTES.gated} element={<Navigate to={ROUTES.explorer} />} />
                <Route path={"/"} element={<Outlet />}>
                  {/* don't want to apply content wrapper here */}
                  <Route path="/" element={<ContentWrapper />}>
                    <Route path={ROUTES.portfolio} element={<PortfolioPage />} />
                    <Route path={ROUTES.explorer} element={<GroupPage />} />
                    <Route path={ROUTES.trade} element={<TradePageV2 />} />
                    <Route path={ROUTES.cabalId} element={<GroupIdPage />} />
                    <Route path={ROUTES.tokenId} element={<TokenIdPage />} />
                    <Route path={ROUTES.referral} element={<ReferralPageV2 />} />
                    <Route path={ROUTES.airdrop} element={<Airdrop />} />
                    <Route path={ROUTES.airdropGroup} element={<AirdropGroup />} />
                    <Route path={ROUTES.shill} element={<ShillDashboard />} />
                    <Route path={ROUTES.redirect} element={<RedirectPage />} />

                    {/* <Route path={ROUTES.create} element={<CreatePage />} /> */}
                    {/* <Route path={ROUTES.explorer} element={<ExplorerPage />} /> */}
                    {/* <Route path={ROUTES.call} element={<CallPage />} /> */}
                    {process.env.REACT_APP_ENV === "dev" ? (
                      <>
                        <Route path={ROUTES.layout} element={<ReferralPage />} />
                        <Route path={ROUTES.design} element={<DesignSystemPage />} />
                      </>
                    ) : (
                      <></>
                    )}
                  </Route>
                </Route>
              </Route>
            </Routes>
          </ScreensWrapper>
          {!ExclueFooterRoutes.includes(location.pathname) && (
            <Responsive.Layout>
              <Responsive.View to="md">
                <Footer />
              </Responsive.View>
            </Responsive.Layout>
          )}
        </AppWrapper>
        <OnboardingViewV3 />
      </EnvContext.Provider>
    </QueryClientProvider>
  );
};

export default App;
